import { type PropsWithChildren, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { MoreVertical } from 'lucide-react';
import { Link } from 'react-router-dom';

import type {
  UserCard_UserFragment,
  Users_Bool_Exp,
  Users_Order_By,
} from '@graphql/__generated__/graphql';
import { AddUserModal } from '@modules/_shared/components/AddUserModal';
import { ContactQuickActions } from '@modules/_shared/components/ContactQuickActions';
import { MutationErrorModal } from '@modules/_shared/components/MutationErrorModal';
import { usePhoneCopyMenuItems } from '@modules/_shared/components/usePhoneCopyMenuItems';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import {
  COMPACT_USER_AVATAR_MIN_WIDTH,
  COMPACT_USER_AVATAR_SIZE,
  UserAvatar,
} from '@modules/_shared/components/UserAvatar';
import {
  UserCard,
  UserCardSkeleton,
} from '@modules/_shared/components/UserCard';
import { useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

export type TEditUserInputProps = {
  orderResultsBy?: Users_Order_By[];
  filters?: Users_Bool_Exp;
};

interface UserActionCardProps {
  user?: UserCard_UserFragment | null;
  canDelete?: boolean;
  useLegacyLinks?: boolean;
  editUserInputProps?: TEditUserInputProps;
  secondaryText?: React.ReactNode;
  isLoading?: boolean;
  creatable?: boolean;
  readOnly?: boolean;
  compact?: boolean;
  onUserChanged: (
    userId: string | null,
    onError: (err: ErrorLike) => void,
  ) => Promise<any>;
}

export const EditableUserCard: React.FC<
  {
    selectedUserId?: string;
    isEditing: boolean;
    onChange: (userId: string) => Promise<any>;
    creatable?: boolean;
    onCancel: () => void;
  } & TEditUserInputProps &
    PropsWithChildren
> = ({
  selectedUserId,
  children,
  isEditing,
  onChange,
  onCancel,
  orderResultsBy,
  filters,
  creatable = false,
}) => {
  const { t } = useLocale();
  const [isLoading, setIsLoading] = useState(false);
  const [userCreationData, setUserCreationData] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null>(null);

  return isEditing ? (
    <Stack sx={{ position: 'relative' }} direction="row" spacing={1}>
      <UserSelect
        sx={{ flexGrow: 1 }}
        disabled={isLoading}
        userId={selectedUserId}
        orderResultsBy={orderResultsBy}
        filters={filters}
        autoFocus={true}
        creatable={creatable}
        onCreate={userData => {
          const newUser: {
            firstName?: string;
            lastName?: string;
            email?: string;
          } = {};

          if (userData.first_name != null) {
            newUser.firstName = userData.first_name;
          }

          if (userData.last_name != null) {
            newUser.lastName = userData.last_name;
          }

          if (userData.email != null) {
            newUser.email = userData.email;
          }

          setUserCreationData(newUser);
        }}
        onChange={async userId => {
          if (userId == null) {
            return;
          }

          setIsLoading(true);
          await onChange(userId);
          setIsLoading(false);
        }}
      />
      <Button
        variant="outlined"
        onClick={onCancel}
        disabled={isLoading}
        size="small"
      >
        {t('Cancel')}
      </Button>

      {creatable && (
        <AddUserModal
          opened={userCreationData != null}
          user={userCreationData ?? undefined}
          onClose={async user => {
            setUserCreationData(null);

            if (user != null) {
              setIsLoading(true);
              await onChange(user.id);
              setIsLoading(false);
            }
          }}
        />
      )}

      {isLoading && (
        <CircularProgress
          size={30}
          sx={{
            zIndex: 1,
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-15px !important',
            marginLeft: '-15px !important',
          }}
          disableShrink
        />
      )}
    </Stack>
  ) : (
    <>{children}</>
  );
};

export const UserActionCard: React.FC<UserActionCardProps> = ({
  user,
  onUserChanged,
  canDelete = false,
  useLegacyLinks = false,
  editUserInputProps = {},
  secondaryText,
  isLoading = false,
  creatable = false,
  readOnly = false,
  compact = false,
}) => {
  const { t } = useLocale();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [toggleError, setToggleError] = useState<null | ErrorLike>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const phoneCopyMenuItems = usePhoneCopyMenuItems(user?.phone_numbers ?? []);

  if (isLoading) {
    return <UserCardSkeleton condensed hasSecondaryText />;
  }

  const open = Boolean(anchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const updateUser = async (userId: string | null) => {
    await onUserChanged(userId, setToggleError);
    setIsEditing(false);
  };

  if (user == null) {
    if (readOnly) {
      return null;
    }
    return (
      <EditableUserCard
        isEditing={isEditing}
        onChange={updateUser}
        onCancel={() => setIsEditing(false)}
        creatable={creatable}
        {...editUserInputProps}
      >
        <List dense={true} sx={{ p: 0 }}>
          <ListItem sx={{ py: 0 }} disableGutters>
            <ListItemButton sx={{ p: 0 }} onClick={() => setIsEditing(true)}>
              <ListItemAvatar
                sx={{
                  minWidth: compact ? COMPACT_USER_AVATAR_MIN_WIDTH : undefined,
                }}
              >
                <UserAvatar
                  user={null}
                  size={compact ? COMPACT_USER_AVATAR_SIZE : 40}
                />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant={compact ? 'body2' : 'body1'}>
                    {t('Assign an user')}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        </List>
      </EditableUserCard>
    );
  }

  const userLink = useLegacyLinks
    ? `/v1/users/${toGlobalId('User', user.id)}`
    : `/users/${user.id}`;

  return (
    <>
      <EditableUserCard
        selectedUserId={user.id}
        isEditing={isEditing}
        onChange={updateUser}
        creatable={creatable}
        onCancel={() => setIsEditing(false)}
        {...editUserInputProps}
      >
        <Box sx={{ position: 'relative' }}>
          <UserCard
            user={user}
            condensed
            compact={compact}
            secondaryText={secondaryText}
            userActions={
              compact ? (
                !readOnly && (
                  <IconButton edge="end" size="small" onClick={handleOpenMenu}>
                    <MoreVertical size={20} />
                  </IconButton>
                )
              ) : (
                <>
                  <ContactQuickActions
                    phoneNumbers={user.phone_numbers}
                    emails={user.emails}
                  />
                  {!readOnly && (
                    <IconButton edge="end" onClick={handleOpenMenu}>
                      <MoreVertical size={20} />
                    </IconButton>
                  )}
                </>
              )
            }
          />
          {isDeleting && (
            <Backdrop open={true} sx={{ zIndex: 1, position: 'absolute' }}>
              <CircularProgress disableShrink />
            </Backdrop>
          )}
        </Box>
      </EditableUserCard>
      <Menu
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
      >
        {phoneCopyMenuItems}
        <MenuItem component={Link} to={userLink}>
          {t('See profile')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setIsEditing(true);
            handleCloseMenu();
          }}
        >
          {t('Edit')}
        </MenuItem>
        {canDelete && (
          <MenuItem
            onClick={async () => {
              setIsDeleting(true);
              handleCloseMenu();
              await updateUser(null);
              setIsDeleting(false);
            }}
          >
            {t('remove')}
          </MenuItem>
        )}
      </Menu>
      <MutationErrorModal
        error={toggleError}
        onClose={() => setToggleError(null)}
      />
    </>
  );
};
